// Ассистент «Спросить CRM» — TanStack Query.
// Ключ включает tenantId (docs/stack/07-frontend-vite.md §4).
//
// ПОЧЕМУ БЕЗ ОПТИМИЗМА. Ответ приходит секунды и стоит денег: показать вопрос
// заранее можно, ответ — нет. Поэтому вопрос кладём в ленту сразу, а ответ
// ждём и дорисовываем.

import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';
import { useRef } from 'react';
import { useLocation } from 'react-router-dom';

import { useTenantId } from '@/lib/tenant-context';

import {
  askAssistant,
  createAssistantThread,
  getAssistantStatus,
  listAssistantMessages,
  listAssistantThreads,
  type AssistantMessage,
  type AssistantStatus,
  type AssistantThread,
} from '../api/assistant.api';

export function assistantKeys(tenantId: string | null) {
  const root = ['assistant', tenantId] as const;
  return {
    root,
    status: [...root, 'status'] as const,
    threads: [...root, 'threads'] as const,
    messages: (threadId: string) => [...root, 'messages', threadId] as const,
  };
}

export function useAssistantStatus(): UseQueryResult<AssistantStatus, Error> {
  const tenantId = useTenantId();
  return useQuery({
    queryKey: assistantKeys(tenantId).status,
    queryFn: getAssistantStatus,
    // Настройка меняется только выкатом — спрашивать чаще незачем.
    staleTime: 10 * 60 * 1000,
  });
}

export function useAssistantThreads(enabled: boolean): UseQueryResult<AssistantThread[], Error> {
  const tenantId = useTenantId();
  return useQuery({
    queryKey: assistantKeys(tenantId).threads,
    queryFn: listAssistantThreads,
    enabled,
  });
}

export function useAssistantMessages(
  threadId: string | null,
): UseQueryResult<AssistantMessage[], Error> {
  const tenantId = useTenantId();
  return useQuery({
    queryKey: assistantKeys(tenantId).messages(threadId ?? 'none'),
    queryFn: () => listAssistantMessages(threadId as string),
    enabled: Boolean(threadId),
  });
}

export function useCreateThread(): UseMutationResult<AssistantThread, Error, void> {
  const tenantId = useTenantId();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: createAssistantThread,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: assistantKeys(tenantId).threads });
    },
  });
}

interface AskVars {
  threadId: string;
  text: string;
  screen?: string;
  idempotencyKey: string;
}

export function useAskAssistant(): UseMutationResult<
  { question: AssistantMessage; answer: AssistantMessage },
  Error,
  AskVars
> {
  const tenantId = useTenantId();
  const qc = useQueryClient();

  return useMutation({
    mutationFn: (vars: AskVars) =>
      askAssistant(vars.threadId, {
        text: vars.text,
        ...(vars.screen ? { screen: vars.screen } : {}),
        idempotencyKey: vars.idempotencyKey,
      }),
    onSuccess: (data, vars) => {
      const key = assistantKeys(tenantId).messages(vars.threadId);
      qc.setQueryData<AssistantMessage[]>(key, (prev) => [
        ...(prev ?? []),
        data.question,
        data.answer,
      ]);
      void qc.invalidateQueries({ queryKey: assistantKeys(tenantId).threads });
    },
  });
}

/**
 * Экран, с которого спрашивают: раздел и номер открытой карточки.
 *
 * ПОЧЕМУ РАЗБИРАЕМ ПУТЬ САМИ, А НЕ ЧЕРЕЗ `matchRoutes`. Реестр маршрутов тянет
 * за собой Layout, а Layout — этот виджет: получается цикл импортов, который
 * линтер отклоняет справедливо. Разделов с числовым идентификатором у нас
 * ровно один вид — короткие номера карточек (план 116), и их достаточно.
 *
 * Имени здесь нет намеренно: уезжает только номер, а имя резолвит сервер под
 * правами сотрудника.
 */
export function useScreenContext(): string | undefined {
  const { pathname } = useLocation();

  const [section, second] = pathname.split('/').filter(Boolean);
  if (!section) return undefined;
  return second && /^\d+$/.test(second) ? `/${section} #${second}` : `/${section}`;
}

/**
 * Ключ идемпотентности вопроса.
 *
 * Живёт, пока текст не изменился: повтор после обрыва обязан вернуть уже
 * оплаченный ответ, а правка текста — купить новый. Ровно та же логика, что у
 * ключей денежных форм (гоча про двойную проводку при обрыве сети).
 */
export function useQuestionKey(text: string): string {
  const ref = useRef<{ text: string; key: string }>({ text: '', key: '' });
  if (ref.current.text !== text) {
    ref.current = { text, key: crypto.randomUUID() };
  }
  return ref.current.key;
}
